import React from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import ResizableImage from "../../elements/ResizableImage";
import OrangeHighlightButton from "../../elements/OrangeHighlightButton";

export interface SparrowSectionProps {
  image: string;
  title1: string;
  description1?: string;
  title2?: string;
  description2?: string;
  title3?: string;
  description3?: string;
  description4?: string;
  reverse?: boolean;
  marginTop?: boolean;
  marginBottom?: boolean;
  border?: boolean;
  changeColor?: boolean;
  buttonText?: string;
}

const SparrowSection: React.FC<SparrowSectionProps> = ({
  image,
  title1,
  description1,
  title2,
  description2,
  title3,
  description3,
  description4,
  reverse = false,
  marginTop = false,
  marginBottom = false,
  border = false,
  changeColor = false,
  buttonText,
}) => {
  const history = useHistory();

  const { i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleRedirection = () => {
    const path =
      currentLanguage === "en" ? "/enroll" : `/${currentLanguage}/enroll`;
    history.push(path);
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.primary.main,
        px: isMobile ? "20px" : "10%",
        pt: marginTop ? (isMobile ? "6vh" : "16vh") : 0,
        pb: marginBottom ? (isMobile ? "6vh" : "10vh") : 0,
      }}
    >
      <Grid
        container
        direction={reverse ? "row-reverse" : "row"}
        alignItems="center"
        justifyContent="center"
        padding={4}
        sx={{
          borderRadius: border ? "25px" : 0,
          boxShadow: border ? "4px 4px 6px 0px rgba(0, 0, 0, 0.5)" : "none",
        }}
      >
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            display: "flex",
            justifyContent: "center",
            pb: isMobile ? 4 : 0,
          }}
        >
          <ResizableImage src={image} />
        </Grid>
        <Grid item xs={12} md={1}></Grid>
        <Grid item xs={12} md={8}>
          <Typography
            variant="h4"
            gutterBottom
            textAlign={isMobile ? "center" : "left"}
            fontWeight={600}
            mb={4}
            color={
              changeColor
                ? theme.palette.secondary.main
                : theme.palette.text.secondary
            }
          >
            {title1}
          </Typography>
          {description1 && (
            <Typography
              variant="h5"
              fontWeight={400}
              mb={4}
              textAlign={isMobile ? "center" : "left"}
            >
              {description1}
            </Typography>
          )}
          {title2 && (
            <Typography
              variant="h4"
              gutterBottom
              textAlign={isMobile ? "center" : "left"}
              fontWeight={600}
              mb={4}
              color={
                changeColor
                  ? theme.palette.secondary.main
                  : theme.palette.text.secondary
              }
            >
              {title2}
            </Typography>
          )}
          {description2 && (
            <Typography
              variant="h5"
              fontWeight={400}
              mb={4}
              textAlign={isMobile ? "center" : "left"}
            >
              {description2}
            </Typography>
          )}
          {title3 && (
            <Typography
              variant="h4"
              gutterBottom
              textAlign={isMobile ? "center" : "left"}
              fontWeight={600}
              mb={4}
              color={theme.palette.text.secondary}
            >
              {title3}
            </Typography>
          )}
          {description3 && (
            <Typography
              variant="h5"
              gutterBottom
              textAlign={isMobile ? "center" : "left"}
              mb={4}
            >
              {description3}
            </Typography>
          )}
          {description4 && (
            <Typography
              variant="h5"
              gutterBottom
              textAlign={isMobile ? "center" : "left"}
              mb={4}
            >
              {description4}
            </Typography>
          )}
          {buttonText && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: isMobile ? "center" : "start",
              }}
            >
              <OrangeHighlightButton onClick={handleRedirection} sx={{ mt: 4 }}>
                {buttonText}
              </OrangeHighlightButton>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SparrowSection;
